import type { ColumnOrderState, VisibilityState } from '@tanstack/react-table';

/**
 * Convert hidden columns array to TanStack VisibilityState.
 * TanStack expects { columnId: false } for hidden columns.
 * Columns not in the object are considered visible.
 *
 * @example
 * hiddenColumnsToVisibilityState(['col1', 'col2'])
 * // Returns: { col1: false, col2: false }
 */
export const hiddenColumnsToVisibilityState = (hiddenColumns: string[]): VisibilityState => {
  const visibility: VisibilityState = {};

  hiddenColumns.forEach((key) => {
    visibility[key] = false;
  });

  return visibility;
};

/**
 * Convert TanStack VisibilityState back to hidden columns array.
 * Extracts only columns that are explicitly set to false.
 *
 * @example
 * visibilityStateToHiddenColumns({ col1: false, col2: false, col3: true })
 * // Returns: ['col1', 'col2']
 */
export const visibilityStateToHiddenColumns = (state: VisibilityState): string[] => {
  return Object.entries(state)
    .filter(([_, visible]) => visible === false)
    .map(([key]) => key);
};

/**
 * Type for TanStack updater pattern - can be either a value or a function
 */
export type StateUpdater<T> = T | ((prev: T) => T);

/**
 * Helper to resolve an updater to a value.
 * TanStack's onChange handlers accept both direct values and updater functions.
 */
export const resolveUpdater = <T>(updater: StateUpdater<T>, prevValue: T): T => {
  if (typeof updater === 'function') {
    return (updater as (prev: T) => T)(prevValue);
  }

  return updater;
};

// Re-export TanStack types for convenience
export type { ColumnOrderState, VisibilityState };
